import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useTheme } from '@/components/theme-provider';
import { useLanguage } from '@/contexts/LanguageContext';
import { Sun, Moon, Monitor } from 'lucide-react';

interface ThemeStepProps {
  onNext: () => void;
  onBack: () => void;
}

type Theme = 'light' | 'dark' | 'system';

/**
 * Step for setting the user's preferred theme during onboarding
 */
export function ThemeStep({ onNext, onBack }: ThemeStepProps) {
  const { t } = useLanguage();
  const { theme, setTheme } = useTheme();

  const handleThemeChange = (newTheme: string) => {
    setTheme(newTheme as Theme);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onNext();
  };

  const themeOptions = [
    {
      value: 'light' as Theme,
      label: t.userOnboardingWizard.themeStep.light,
      description: t.userOnboardingWizard.themeStep.lightDescription,
      icon: Sun,
    },
    {
      value: 'dark' as Theme,
      label: t.userOnboardingWizard.themeStep.dark,
      description: t.userOnboardingWizard.themeStep.darkDescription,
      icon: Moon,
    },
    {
      value: 'system' as Theme,
      label: t.userOnboardingWizard.themeStep.system,
      description: t.userOnboardingWizard.themeStep.systemDescription,
      icon: Monitor,
    },
  ];

  return (
    <Card className="h-[480px] flex flex-col">
      <CardHeader>
        <CardTitle>{t.userOnboardingWizard.themeStep.title}</CardTitle>
        <CardDescription>{t.userOnboardingWizard.themeStep.description}</CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit} className="flex flex-col flex-1">
        <CardContent className="space-y-4 flex-1">
          <RadioGroup value={theme} onValueChange={handleThemeChange} className="grid gap-4">
            {themeOptions.map((option) => (
              <div key={option.value}>
                <RadioGroupItem
                  value={option.value}
                  id={option.value}
                  className="peer sr-only"
                />
                <Label
                  htmlFor={option.value}
                  className="flex items-center gap-4 rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                >
                  <option.icon className="h-6 w-6" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium leading-none">{option.label}</p>
                    <p className="text-sm text-muted-foreground">{option.description}</p>
                  </div>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </CardContent>
        <CardFooter className="flex justify-between pt-4">
          <Button type="button" variant="outline" onClick={onBack}>
            {t.userOnboardingWizard.themeStep.back}
          </Button>
          <Button type="submit">{t.userOnboardingWizard.themeStep.continue}</Button>
        </CardFooter>
      </form>
    </Card>
  );
}
